import React, { useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import AuthShell from '../Layout/AuthShell';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const validateEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (!email.trim()) {
        setMessage({ type: 'error', text: 'Please enter your email address' });
        return;
      }

      if (!validateEmail(email)) {
        setMessage({ type: 'error', text: 'Please enter a valid email address' });
        return;
      }

      const response = await fetch(API_ENDPOINTS.AUTH_FORGOT_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (response.ok) {
        setMessage({
          type: 'success',
          text: 'If that email belongs to an account, password reset instructions have been sent.',
        });
        setEmail('');
      } else {
        try {
          const errorData = await response.json();
          setMessage({
            type: 'error',
            text: errorData.message || 'Failed to send password reset instructions',
          });
        } catch {
          setMessage({
            type: 'error',
            text: `Failed to send password reset instructions (${response.status})`,
          });
        }
      }
    } catch (error) {
      console.error('Error sending password reset request:', error);
      setMessage({
        type: 'error',
        text: 'An error occurred while sending password reset instructions',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setMessage(null);
  };

  return (
    <AuthShell
      title="Reset access"
      description="Tell us which email owns the account and we will send a secure password-reset link."
      footer={(
        <div className="auth-link-row">
          <Link to="/login" className="auth-link">Back to login</Link>
        </div>
      )}
    >
      {message ? (
        <Alert variant={message.type === 'success' ? 'success' : 'danger'}>
          {message.text}
        </Alert>
      ) : null}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            className="auth-control"
            type="email"
            value={email}
            onChange={handleInputChange}
            placeholder="Enter your email address"
            required
            autoComplete="email"
          />
        </Form.Group>

        <p className="auth-helper-text mb-0">
          We will email instructions to this address. If the account exists, the reset link will arrive shortly.
        </p>

        <div className="d-grid">
          <Button
            variant="primary"
            type="submit"
            disabled={loading || !email.trim()}
            className="auth-primary-btn mt-4"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Sending...
              </>
            ) : (
              'Send Reset Instructions'
            )}
          </Button>
        </div>
      </Form>
    </AuthShell>
  );
};

export default ForgotPassword;
